import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Shield } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const productLinks = [
  { label: "Investment Products", to: "/investments", desc: "Mutual Funds · SIPs · Bonds · Stocks" },
  { label: "Forex Trading", to: "/forex", desc: "60+ pairs with institutional spreads" },
  { label: "Gold Investment", to: "/gold", desc: "Digital Gold · SGBs · Physical" },
  { label: "Travel Services", to: "/travel", desc: "Premium metal cards · Lounges" },
];

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#030712]/80 border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mfb-container flex h-16 sm:h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <div className="relative w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500 via-sky-600 to-[#0A1128]" />
            <span className="relative font-bold text-white tracking-tight text-sm">M</span>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-[#D4AF37] ring-2 ring-[#030712]" />
          </div>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold tracking-tight text-white font-[Outfit]">
              My Funding Bank
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sky-400/80">Wealth · AI · Gold</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.slice(0, 2).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-full transition-colors ${
                  isActive ? "text-white" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-products-trigger"
              className="px-4 py-2 text-sm rounded-full text-slate-300 hover:text-white flex items-center gap-1 transition-colors outline-none"
            >
              Products <ChevronDown className="w-3.5 h-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-80 glass-strong border-white/10 mt-2"
              data-testid="nav-products-menu"
            >
              <DropdownMenuLabel className="text-xs uppercase tracking-widest text-sky-400">
                Wealth Suite
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              {productLinks.map((p) => (
                <DropdownMenuItem
                  key={p.to}
                  onSelect={() => navigate(p.to)}
                  data-testid={`nav-product-${p.to.replace("/", "")}`}
                  className="cursor-pointer flex-col items-start gap-0.5 py-3 focus:bg-white/5"
                >
                  <span className="text-sm font-medium text-white">{p.label}</span>
                  <span className="text-xs text-slate-400">{p.desc}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {navLinks.slice(2).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-full transition-colors ${
                  isActive ? "text-white" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink
            to="/download"
            data-testid="nav-link-download"
            className="px-4 py-2 text-sm rounded-full text-slate-300 hover:text-white"
          >
            Download App
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/admin/login"
            data-testid="nav-admin-login-btn"
            className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 text-xs rounded-full border border-white/10 text-slate-300 hover:text-white hover:border-white/30 transition-all"
          >
            <Shield className="w-3.5 h-3.5" /> Admin Login
          </Link>
          <Link
            to="/contact"
            data-testid="nav-open-account-btn"
            className="hidden sm:inline-flex btn-primary !py-2 !px-4 text-xs"
            onClick={() => trackEvent("open_account_click", { source: "navbar" })}
          >
            Open Account
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/10 text-slate-200"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-strong border-t border-white/10" data-testid="nav-mobile-menu">
          <div className="mfb-container py-4 flex flex-col gap-1">
            {[...navLinks, ...productLinks, { to: "/download", label: "Download App" }, { to: "/admin/login", label: "Admin Login" }].map((l) => (
              <Link
                key={l.to + l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-white/5"
              >
                {l.label}
              </Link>
            ))}
            <Link to="/contact" className="btn-primary mt-3 w-full" onClick={() => setOpen(false)}>
              Open Account
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
